import React, { useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useHalachicData } from '../../hooks/useHalachicData';
import { useHalachicRange } from '../../hooks/useHalachicRange';
import { buildHalachicItems, filterLookaheadDeltas } from './inyaneiUtils';
import templates from './templates';

/**
 * InyaneiHaYomWidget
 * מציג ענייני היום ההלכתיים — תחנון, הלל, תוספות תפילה, מנהגים עונתיים ועוד.
 * מתעדכן אוטומטית בשקיעה ובחצות (דרך useHalachicData).
 */
export const InyaneiHaYomWidget = ({
  edah = 'ashkenaz',
  title = 'ענייני היום',
  showTitle = true,
  template = 'Modern',
  showTachanun = true,
  showHallel = true,
  showPrayerAdditions = true,
  showSeasonalPrayers = true,
  showSeasonalTransitions = true,
  showSelichot = true,
  showWeekdayPrayers = true,
  showShabbatAdditions = true,
  showMotzaeiShabbat = true,
  showSeasonalCustoms = true,
  showLiturgy = true,
  showLeyning = false,
  showHaftarah = false,
  showMourningPeriods = true,
  showLimudim = false,
  lookaheadDays = 0,
  location,
  // Preview mode — used by TemplatePreview for dummy data
  previewItems = null,
  previewHebrewDate = null,
}) => {
  const Template = templates[template] || templates['Modern'];
  const isPreview = !!previewItems;

  // -- hooks must always be called in the same order --------------------------
  const engineConfig = useMemo(() => ({
    location: {
      lat: location?.lat,
      lng: location?.lng,
      timezone: location?.timezone || location?.tzid || 'Asia/Jerusalem',
    },
    isIsrael: true,
    edah: edah === 'mizrahi' ? 'mizrahi' : edah === 'sephardic' ? 'sephardic' : 'ashkenaz',
  }), [location?.lat, location?.lng, location?.timezone, location?.tzid, edah]);

  const { today, loading: engineLoading, error: engineError } = useHalachicData(engineConfig, { autoFetch: !isPreview });

  const needRange = lookaheadDays > 0 && !isPreview;
  const { range, loading: rangeLoading, error: rangeError } = useHalachicRange(
    engineConfig,
    needRange ? lookaheadDays + 1 : 0,
    { autoFetch: needRange },
  );

  const settings = useMemo(() => ({
    showTachanun, showHallel, showPrayerAdditions, showSeasonalPrayers,
    showSeasonalTransitions, showSelichot, showWeekdayPrayers, showShabbatAdditions,
    showMotzaeiShabbat, showSeasonalCustoms, showLiturgy, showLeyning,
    showHaftarah, showMourningPeriods, showLimudim,
  }), [
    showTachanun, showHallel, showPrayerAdditions, showSeasonalPrayers,
    showSeasonalTransitions, showSelichot, showWeekdayPrayers, showShabbatAdditions,
    showMotzaeiShabbat, showSeasonalCustoms, showLiturgy, showLeyning,
    showHaftarah, showMourningPeriods, showLimudim,
  ]);

  const todayItems = useMemo(() => {
    if (isPreview || !today) return [];
    return buildHalachicItems(today, settings, edah);
  }, [isPreview, today, settings, edah]);

  const lookaheadItems = useMemo(() => {
    if (!needRange || !range || range.length === 0) return [];
    return range.slice(1).map((dayData) => {
      const fullItems = buildHalachicItems(dayData, settings, edah);
      // Delta filter: show only items that differ from today
      const deltaItems = filterLookaheadDeltas(todayItems, fullItems);
      return {
        date: dayData.date,
        hebrewDate: dayData.hebrewDate,
        items: deltaItems,
      };
    });
  }, [needRange, range, settings, edah, todayItems]);

  // -- Preview mode -----------------------------------------------------------
  if (isPreview) {
    return (
      <Template
        today={{ hebrewDate: { rendered: previewHebrewDate || '' } }}
        items={previewItems}
        lookaheadItems={[]}
        lookaheadDays={0}
        loading={false}
        error={null}
        title={title}
        showTitle={showTitle}
        edah={edah}
      />
    );
  }

  // -- loading / error states ------------------------------------------------
  if (engineLoading && !today) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress size={24} sx={{ color: '#1e3a5f' }} />
      </Box>
    );
  }

  if (engineError && !today) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography sx={{ fontSize: '0.8rem', color: '#ef4444' }}>שגיאה בטעינת הנתונים</Typography>
      </Box>
    );
  }

  return (
    <Template
      today={today}
      items={todayItems}
      lookaheadItems={lookaheadItems}
      lookaheadDays={lookaheadDays}
      loading={engineLoading || rangeLoading}
      error={engineError || rangeError}
      title={title}
      showTitle={showTitle}
      edah={edah}
    />
  );
};
